import { useTranslate } from '@refinedev/core';
import { Tag, Tooltip } from 'antd';
import type {
  SegmentErrorReason,
  SegmentSettlementStatus,
} from '@/api/generated/schemas';
import {
  CONSUMPTION_STATUS_COLORS,
  PRODUCT_FORMAT_COLORS,
  PRODUCT_VARIANT_COLORS,
  SEGMENT_ERROR_REASON_COLORS,
  SEGMENT_SETTLEMENT_COLORS,
} from './colors';

/** Namespace of the `format.*` / `variant.*` label group to read from. */
export type ProgressLabels =
  | 'sessions'
  | 'progressions'
  | 'segments'
  | 'segment_errors'
  | 'positions';

// ─── ConsumptionStatusTag ─────────────────────────────────────────────────────

export const ConsumptionStatusTag = ({ status }: { status: string }) => {
  const t = useTranslate();
  return (
    <Tag color={CONSUMPTION_STATUS_COLORS[status] ?? 'default'}>
      {t(`progress.progressions.status.${status}`)}
    </Tag>
  );
};

// ─── ProductFormatTag ─────────────────────────────────────────────────────────

/** `labels` picks the i18n namespace — each progress resource ships its own
 * `format.*` group. */
export const ProductFormatTag = ({
  value,
  labels = 'sessions',
}: {
  value: string;
  labels?: ProgressLabels;
}) => {
  const t = useTranslate();
  return (
    <Tag color={PRODUCT_FORMAT_COLORS[value] ?? 'default'}>
      {t(`progress.${labels}.format.${value}`)}
    </Tag>
  );
};

// ─── ProductVariantTag ────────────────────────────────────────────────────────

export const ProductVariantTag = ({
  value,
  labels = 'segments',
}: {
  value: string;
  labels?: ProgressLabels;
}) => {
  const t = useTranslate();
  return (
    <Tag color={PRODUCT_VARIANT_COLORS[value] ?? 'default'}>
      {t(`progress.${labels}.variant.${value}`)}
    </Tag>
  );
};

// ─── SegmentErrorReasonTag ────────────────────────────────────────────────────

/** Rejection reason with the validator rule behind it in the tooltip. */
export const SegmentErrorReasonTag = ({
  value,
}: {
  value: SegmentErrorReason;
}) => {
  const t = useTranslate();
  return (
    <Tooltip title={t(`progress.segment_errors.reason_hints.${value}`)}>
      <Tag color={SEGMENT_ERROR_REASON_COLORS[value] ?? 'default'}>
        {t(`progress.segment_errors.reason.${value}`)}
      </Tag>
    </Tooltip>
  );
};

// ─── SegmentSettlementTag ─────────────────────────────────────────────────────

/** `null` is a meaningful state — the segment still sits in the pending pool —
 * so it gets its own tag rather than a dash. */
export const SegmentSettlementTag = ({
  value,
}: {
  value?: SegmentSettlementStatus | null;
}) => {
  const t = useTranslate();
  const key = value ?? 'pending';
  return (
    <Tag
      color={value ? (SEGMENT_SETTLEMENT_COLORS[value] ?? 'default') : 'gold'}
    >
      {t(`progress.segments.settlement_status.${key}`)}
    </Tag>
  );
};
